import { apiFetch } from '@/lib/api';
import { useOrgStore } from '@/stores/orgStore';
import { GalleryImage } from '@/types/attachment';

export class AttachmentsService {
  private orgHeaders(): Record<string, string> {
    try {
      const stateOrgId = useOrgStore.getState().activeOrgId;
      const storedOrgId = localStorage.getItem('ima_active_org_id');
      const orgId = stateOrgId || storedOrgId || undefined;
      return orgId ? { 'X-Organization-Id': orgId } : {};
    } catch {
      return {};
    }
  }

  async list(q?: string): Promise<GalleryImage[]> {
    const params = new URLSearchParams();
    if (q) params.set('q', q);
    const res = await apiFetch(`/api/attachments?${params.toString()}`, { headers: this.orgHeaders() });
    if (!res.ok) return [];
    const payload = await res.json();
    const files = payload?.data || payload?.items || [];
    return files.map((f: any) => ({
      id: f.id,
      filename: f.filename || f.originalName || f.title || f.stored_filename,
      originalName: f.originalName || f.original_filename || f.title || '',
      // UI sometimes expects originalFilename and thumbnailUrl
      originalFilename: f.originalName || f.original_filename || f.title || '',
      thumbnailUrl: f.thumbnail || f.thumbnail_url || undefined,
      url: f.url,
      thumbnail: f.thumbnail || f.thumbnail_url || undefined,
      title: f.title,
      metadata: {
        uploadedAt: new Date(f.metadata?.uploadedAt || f.created_at || Date.now()).toISOString(),
        size: f.metadata?.size ?? f.size ?? 0,
        width: f.metadata?.width ?? f.width ?? null,
        height: f.metadata?.height ?? f.height ?? null,
        tags: f.metadata?.tags ?? f.tags ?? [],
        mimeType: f.mime_type || f.metadata?.mimeType,
      },
      description: f.description || f.ai_summary || null,
      aiAnalyzedAt: f.aiAnalyzedAt || f.ai_analyzed_at ? new Date(f.aiAnalyzedAt || f.ai_analyzed_at).toISOString() : null,
    }));
  }

  async upload(file: File, options: { title?: string; tags?: string[] } = {}): Promise<GalleryImage> {
    // Step 1: Presign upload
    const presignRes = await apiFetch('/api/attachments/presign', {
      method: 'POST',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ filename: file.name, contentType: file.type || 'application/octet-stream' }),
    });
    if (!presignRes.ok) throw new Error('Failed to presign attachment upload');
    
    const presigned = await presignRes.json();
    const { url, r2Key, id } = presigned.data || {};

    // Step 2: Upload file to R2
    const putRes = await fetch(url, { 
      method: 'PUT', 
      body: file, 
      headers: { 'Content-Type': file.type || 'application/octet-stream' } 
    });
    if (!putRes.ok) throw new Error('Upload to storage failed');

    // Step 3: Finalize upload
    const finRes = await apiFetch('/api/attachments/finalize', {
      method: 'POST',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id,
        title: options.title || file.name.replace(/\.[^.]+$/, ''),
        tags: options.tags || [],
        r2Key,
        originalFilename: file.name,
        size: file.size,
        mimeType: file.type || 'application/octet-stream',
      }),
    });
    if (!finRes.ok) throw new Error('Failed to finalize attachment upload');
    
    const result = await finRes.json();
    return this.mapResponseToAttachment(result.data || result);
  }

  async delete(id: string): Promise<void> {
    const res = await apiFetch(`/api/attachments/${id}`, {
      method: 'DELETE',
      headers: this.orgHeaders(),
    });
    if (!res.ok) throw new Error('Failed to delete attachment');
  }

  async rename(id: string, title: string): Promise<void> {
    const res = await apiFetch(`/api/attachments/${id}`, {
      method: 'PATCH',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ title }),
    });
    if (!res.ok) throw new Error('Failed to rename attachment');
  }

  async update(id: string, data: { title?: string; description?: string; tags?: string[] }): Promise<void> {
    const payload: Record<string, any> = {};
    if (data.title !== undefined) payload.title = data.title;
    if (data.description !== undefined) payload.description = data.description;
    if (data.tags !== undefined) payload.tags = data.tags;

    const res = await apiFetch(`/api/attachments/${id}`, {
      method: 'PATCH',
      headers: { ...this.orgHeaders(), 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!res.ok) throw new Error('Failed to update attachment');
  }

  async analyze(id: string): Promise<void> {
    const res = await apiFetch(`/api/attachments/${id}/analyze`, {
      method: 'POST',
      headers: this.orgHeaders(),
    });
    if (!res.ok) throw new Error('Failed to start attachment analysis');
  }

  private mapResponseToAttachment(f: any): GalleryImage {
    return {
      id: f.id,
      filename: f.filename || f.originalName || f.title || f.stored_filename,
      originalName: f.originalName || f.original_filename || f.title || '',
      url: f.url,
      thumbnail: f.thumbnail || f.thumbnail_url || f.url,
      title: f.title,
      metadata: {
        uploadedAt: new Date(f.metadata?.uploadedAt || f.created_at || Date.now()).toISOString(),
        size: f.metadata?.size ?? f.size ?? 0,
        width: f.metadata?.width ?? f.width ?? null,
        height: f.metadata?.height ?? f.height ?? null,
        tags: f.metadata?.tags ?? f.tags ?? [],
        mimeType: f.mime_type || f.metadata?.mimeType,
      },
      description: f.description || f.ai_summary || null,
      aiAnalyzedAt: f.aiAnalyzedAt || f.ai_analyzed_at ? new Date(f.aiAnalyzedAt || f.ai_analyzed_at).toISOString() : null,
    };
  }
}


